<template>
  <div class="mt-n3 px-2">
    <!-- <v-card class="pl-3"> -->
    <v-row v-if="aginovaMonitorError || isLoading">
      <v-col>
        <ProgressLinearLoaderVue v-if="isLoading" :isLoading="isLoading" />
        <BannerAlertVue v-if="aginovaMonitorError" :message="bannerMessage" />
      </v-col>
    </v-row>
    <v-row v-if="!aginovaMonitorError && !isLoading">
      <v-col cols="12">
        <v-card class="pa-4">
          <!-- Aginova Monitor Details -->
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="AginovaMonitorStatus.deviceName"
              disabled
              variant="outlined"
              density="compact"
              color="primary"
            >
              <template v-slot:label>
                <span class="text--disabled text-caption font-weight-bold">
                  Device Name
                </span>
              </template>
            </v-text-field>

            <v-text-field
              v-model="AginovaMonitorStatus.friendlyName"
              required
              maxlength="100"
              :disabled="isSiteLocationEditMode || isSiteLocationAddMode"
              variant="outlined"
              density="compact"
              color="primary"
              :class="
                isSiteLocationEditMode || isSiteLocationAddMode
                  ? 'text--disabled'
                  : 'text--enabled'
              "
            >
              <template v-slot:label>
                <span
                  class="text-primary text-caption font-weight-bold"
                  :class="{
                    'text--disabled':
                      isSiteLocationEditMode || isSiteLocationAddMode,
                  }"
                >
                  Friendly Name
                </span>
              </template>
            </v-text-field>
            <!-- Site Location Add/Edit -->
            <SiteLocationCard
              :MonitorStatus="AginovaMonitorStatus"
              :isMonitorDirty="isAginovaMonitorDirty"
              :siteLocation="siteLocation"
              :isSiteLocationEditMode="isSiteLocationEditMode"
              :isSiteLocationAddMode="isSiteLocationAddMode"
              :siteLocationsForMonitor="siteLocationsForAginovaMonitor"
              @site-location-edit-mode="toggleEditMode"
              @site-location-add-mode="toggleAddMode"
              @cancel-site-location-edit-mode="handleCancelSiteLocationEditMode"
              @init="init"
              @update:siteLocationName="siteLocation.siteLocationName = $event"
              @update:siteLocationId="
                AginovaMonitorStatus.siteLocation!.id = $event
              "
            />
            <!-- Control Buttons -->
            <v-btn
              class="rounded-2 mr-2"
              color="primary"
              @click="handleSave"
              :disabled="
                !isAginovaMonitorDirty ||
                isSiteLocationEditMode ||
                isSiteLocationAddMode
              "
              :loading="isSaving"
            >
              Save
            </v-btn>

            <v-btn
              class="rounded-2"
              @click="handleCancelButtonClick"
              :disabled="
                isSiteLocationEditMode || isSiteLocationAddMode || isSaving
              "
            >
              {{ getCancelButtonText }}
            </v-btn>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
    <!-- </v-card> -->
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import BannerAlertVue from '@/Core.UI.Domain/Components/BannerAlert.vue';
import ProgressLinearLoaderVue from '@/Core.UI.Domain/Components/ProgressLinearLoader.vue';
import SiteLocationCard from '@/Core.Service.Domain/Monitors/Monitors.Common/Components/SiteLocationCard.vue';
import API from '@/Core.Service.Domain/Monitors/Aginova/API';
import type {
  IAginovaMonitor,
  ISiteLocation,
  IDirtyState,
} from '@/Core.Service.Domain/Monitors/Aginova/types';

const router = useRouter();
const route = useRoute();
const store = useStore();

const form = ref<HTMLFormElement | null>(null);
const valid = ref(true);
const aginovaMonitorError = ref(false);
const isLoading = ref(false);
const isSaving = ref(false);
const AginovaMonitorInfo = ref<IAginovaMonitor>({} as IAginovaMonitor);
const isSiteLocationEditMode = ref(false);
const isSiteLocationAddMode = ref(false);
const siteLocation = ref<ISiteLocation>({} as ISiteLocation);
const isDirty = ref<IDirtyState>({
  friendlyName: false,
  siteLocationName: false,
  siteLocationId: false,
});
const AginovaMonitorStatus = ref<IAginovaMonitor>({} as IAginovaMonitor);

const siteLocationsForAginovaMonitor = computed<ISiteLocation[]>(
  () =>
    store.getters[
      'MonitorsStore/AginovaMonitorsStore/siteLocationsForAginovaMonitor'
    ]
);

const bannerMessage = computed(() => 'There was an error loading this monitor');

const isAginovaMonitorDirty = computed(() =>
  Object.values(isDirty.value).some((value) => value)
);

const getCancelButtonText = computed(() => {
  if (
    isAginovaMonitorDirty.value &&
    !isSiteLocationEditMode.value &&
    !isSiteLocationAddMode.value
  ) {
    return 'Reset';
  } else {
    return 'Go Back';
  }
});

watch(
  () => AginovaMonitorStatus.value.friendlyName,
  (newVal, oldVal) => {
    isDirty.value.friendlyName = oldVal !== undefined && newVal !== oldVal;
  }
);

watch(
  () => AginovaMonitorStatus.value.siteLocation?.id,
  (newVal, oldVal) => {
    isDirty.value.siteLocationId = oldVal !== undefined && newVal !== oldVal;
  }
);

watch(
  () => siteLocation.value.siteLocationName,
  (newVal, oldVal) => {
    isDirty.value.siteLocationName = oldVal !== undefined && newVal !== oldVal;
  }
);

const getSiteLocationsForAginovaMonitor = async (siteId: number) => {
  return store.dispatch(
    'MonitorsStore/AginovaMonitorsStore/getSiteLocationsForAginovaMonitor',
    siteId
  );
};

const init = async () => {
  try {
    isLoading.value = true;
    const aginovaMonitor = await API.getAginovaMonitorByDeviceId(
      route.params.id as string,
      Number(route.params.deviceSubTypeId)
    );

    const { siteId } = aginovaMonitor;
    await getSiteLocationsForAginovaMonitor(siteId);

    AginovaMonitorInfo.value = {
      ...mapAginovaMonitorBySiteLocationId(aginovaMonitor),
    };

    cloneAginovaMonitorInfo();
    resetDirty();
  } catch (error) {
    aginovaMonitorError.value = true;
    return Promise.reject(error);
  } finally {
    isLoading.value = false;
  }
};

const resetDirty = () => {
  isDirty.value.friendlyName = false;
  isDirty.value.siteLocationName = false;
  isDirty.value.siteLocationId = false;
};

const handleCancel = () => {
  cloneAginovaMonitorInfo();
  nextTick(() => {
    resetDirty();
  });
};

const handleGoBack = () => {
  router.push({ name: 'AginovaMonitorsList' });
};

const handleSave = async () => {
  if (!isAginovaMonitorDirty.value) {
    return;
  }
  isSaving.value = true;
  try {
    console.log(AginovaMonitorStatus.value);
    await API.updateAginovaMonitor(AginovaMonitorStatus.value);
    await init();
    nextTick(() => {
      resetDirty();
    });
  } catch (error) {
    aginovaMonitorError.value = true;
    return Promise.reject(error);
  } finally {
    isSaving.value = false;
  }
};

const cloneAginovaMonitorInfo = () => {
  AginovaMonitorStatus.value = {
    ...AginovaMonitorInfo.value,
    siteLocation: AginovaMonitorInfo.value.siteLocation
      ? { ...AginovaMonitorInfo.value.siteLocation }
      : undefined,
  };
  if (AginovaMonitorInfo.value.siteLocation) {
    siteLocation.value = { ...AginovaMonitorInfo.value.siteLocation };
  }
};

const mapAginovaMonitorBySiteLocationId = (
  aginovaMonitor: IAginovaMonitor
): IAginovaMonitor => {
  const siteLocationMatch = siteLocationsForAginovaMonitor.value.filter(
    (sl: ISiteLocation) => {
      return Number(sl.id) === Number(aginovaMonitor.siteLocation?.id);
    }
  );

  if (siteLocationMatch.length) {
    siteLocation.value = siteLocationMatch[0];
    aginovaMonitor.siteLocation!.siteLocationName =
      siteLocationMatch[0].siteLocationName;
  }

  return aginovaMonitor;
};

const handleCancelSiteLocationEditMode = () => {
  isSiteLocationEditMode.value = false;
  isSiteLocationAddMode.value = false;
  cloneAginovaMonitorInfo();
};

const toggleEditMode = (value: boolean) => {
  isSiteLocationEditMode.value = value;
};

const toggleAddMode = (value: boolean) => {
  isSiteLocationAddMode.value = value;
};

const handleCancelButtonClick = () => {
  if (
    isAginovaMonitorDirty.value &&
    !isSiteLocationEditMode.value &&
    !isSiteLocationAddMode.value
  ) {
    handleCancel();
  } else {
    handleGoBack();
  }
};

onMounted(() => {
  init();
});
</script>
<style lang="scss" scoped>
:deep(.text--enabled) {
  input {
    color: $primary !important;
  }
}
</style>
